import React, { useState } from "react";
import cn from "classnames";
import LazyLoad from "react-lazyload";
import Skeleton from "components/Skeleton/Block";
import { formatNumber } from "util/strings";
import SaveIcon from "components/Save/Icon";
import generateSubtitle from "components/ClientSearch/Listings/ListingCard/GenerateSubTitle";
import renderPricing from "components/ClientSearch/Listings/ListingCard/RenderPricing";
import { publicListingNiceSearch_publicListingNiceSearch_listings_nodes } from "../../Listings/types/publicListingNiceSearch";
import styles from "./style.module.scss";

const MID_DOT = "\u00B7";

const formatLeaseType = (leaseType: string) => {
  switch (leaseType) {
    case "SUBLEASE":
      return ` ${MID_DOT} Sublease`;
    case "DIRECT":
      return ` ${MID_DOT} Direct`;
    default:
      return "";
  }
};

interface IProps {
  listing: publicListingNiceSearch_publicListingNiceSearch_listings_nodes;
  signUpType: string;
  source: string;
  maxMonthlyPrice?: number;
  maxPricePerSquareFoot?: number;
  className?: string;
  areaExternalIds: string[];
  marketOfInterest?: string | null;
  leaseTermMin?: number | null;
  leaseTermMax?: number | null;
  minPricePerSquareFoot?: number | null;
  minSqft?: number | null;
  maxSqft?: number | null;
  minMonthlyPrice?: number | null;
  numEmployees?: string | null;
  phoneRequired?: boolean | null;
  trackingEvent?: () => void;
  onSuccess: (visitSpace: boolean) => void;
}

const ListingCard = ({
  listing,
  maxMonthlyPrice = 0,
  maxPricePerSquareFoot = 0,
  className = "",
  signUpType,
  source,
  areaExternalIds,
  marketOfInterest,
  leaseTermMin,
  leaseTermMax,
  minPricePerSquareFoot,
  minSqft,
  maxSqft,
  minMonthlyPrice,
  numEmployees,
  phoneRequired,
  trackingEvent = () => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSuccess = (visitSpace: boolean) => {},
}: IProps) => {
  const [showMap, setShowMap] = useState(false);

  const listingImageClasses = cn(styles.image, "card-image", {
    [styles.hideListingImage]: showMap,
  });

  const brokerInsight =
    listing.brokerInsights &&
    listing.brokerInsights.length > 0 &&
    listing.brokerInsights[0];

  return (
    <>
      <div className={cn(styles.card, className)}>
        <div className={styles.imageContainer}>
          <LazyLoad
            once // Once the lazy loaded component is loaded, do not detect scroll/resize event anymore.
            debounce
            height="100%"
            placeholder={<Skeleton className={styles.imageSkeleton} />}
            offset={100}
          >
            {listing.image2?.url && (
              <>
                <div className={listingImageClasses}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={listing.image2.url}
                    alt={`Listing at ${listing.building.address} - ${listing.floorAndSuite}`}
                  />
                </div>
                <div className={styles.mapPin}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/nextpublic/map-pin.svg"
                    alt="map-pin"
                    onMouseLeave={() => setShowMap(false)}
                    onMouseOver={() => setShowMap(true)}
                    onFocus={() => setShowMap(true)}
                  />
                </div>
              </>
            )}
          </LazyLoad>
        </div>
        <div className={styles.inner}>
          <p className={`${styles.title}`}>
            {listing.building.address} - {listing.floorAndSuite}
          </p>
          <p className={styles.subtitle}>
            {generateSubtitle(listing.building)}
            {formatLeaseType(listing.leaseType || "")}
          </p>
          <p className={styles.pricing}>
            {renderPricing(listing, maxMonthlyPrice, maxPricePerSquareFoot)}
          </p>
          <p className={styles.details}>
            <i className="icon icon-layout" />
            <span className={styles.squarefeet}>
              {formatNumber(listing.squareFeet)} sf
            </span>
            <i className="icon icon-user" />
            <span className={styles.maxOccupancy}>
              {listing.maxOccupancy
                ? formatNumber(listing.maxOccupancy)
                : `~${formatNumber(listing.estimatedMaxOccupancy)}`}
            </span>
          </p>
          {brokerInsight && (
            <div className={styles.brokerInsightPreview}>
              <span>&quot;</span>
              <p className={styles.brokerInsightPara}>
                {brokerInsight.content}
              </p>
              <span>&quot;</span>
            </div>
          )}
        </div>
      </div>
      {/* this stopPropagation event listener stops the click events
          occurring in the SaveIcon component from trigger the link it's
          nested in. */}
      {/* eslint-disable jsx-a11y/no-static-element-interactions */}
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        className={styles.saveIconContainer}
      >
        {/* eslint-enable jsx-a11y/no-static-element-interactions */}
        <SaveIcon
          id={listing.id}
          buildingId={listing.building.id}
          className={styles.saveIcon}
          signUpType={signUpType}
          source={source}
          areaExternalIds={areaExternalIds}
          marketOfInterest={marketOfInterest}
          leaseTermMin={leaseTermMin}
          leaseTermMax={leaseTermMax}
          minPricePerSquareFoot={minPricePerSquareFoot}
          maxPricePerSquareFoot={maxPricePerSquareFoot}
          minSqft={minSqft}
          maxSqft={maxSqft}
          minMonthlyPrice={minMonthlyPrice}
          maxMonthlyPrice={maxMonthlyPrice}
          numEmployees={numEmployees}
          phoneRequired={phoneRequired}
          trackingEvent={trackingEvent}
          onSuccess={onSuccess}
        />
      </div>
    </>
  );
};

export default ListingCard;
